/**
 * CollectionMultiChatUI Component
 * ChatGPT-style multi-conversation interface for collection chat
 */

import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo
} from 'react';
import {
  Box,
  Typography,
  TextField,
  IconButton,
  CircularProgress,
  useMediaQuery,
  useTheme,
  Paper,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Divider,
  Tooltip,
  Button,
  Menu,
  MenuItem,
  ListItemIcon
} from '@mui/material';
import {
  SmartToy as BotIcon,
  Add as AddIcon,
  Menu as MenuIcon,
  MoreVert as MoreVertIcon,
  Delete as DeleteIcon,
  Chat as ChatIcon,
  CheckCircle as CheckCircleIcon
} from '@mui/icons-material';
import { ArrowUp } from '@phosphor-icons/react';
import { v4 as uuidv4 } from 'uuid';
import { useAuth0 } from '@auth0/auth0-react';
import { useAppUser } from '@/contexts/AppUserProvider';
import { Client } from '@langchain/langgraph-sdk';
import ReactMarkdown from 'react-markdown';
import { format, parseISO } from 'date-fns';
import {
  createConversation,
  getCollectionConversations,
  getConversationById,
  deleteConversation,
  updateConversationThreadId
} from '@/services/collectionChatService';
import {
  CollectionConversation,
  CollectionChatMessage,
  CollectionChatRole,
  CollectionChatMessageChunk
} from '@/types/collectionChatTypes';

const LANGGRAPH_BASE_URL =
  import.meta.env.VITE_LANGGRAPH_BASE_URL || 'http://localhost:2024';
const SIDEBAR_WIDTH = 280;

interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: string;
  isStreaming?: boolean;
  chunks?: CollectionChatMessageChunk[];
}

interface CollectionMultiChatUIProps {
  collectionId: string;
  compact?: boolean;
}

const getAuthToken = async (
  getAccessTokenSilently: any,
  getAccessTokenWithPopup: any
): Promise<string> => {
  const authParams = {
    authorizationParams: {
      audience: import.meta.env.VITE_API_AUTH0_AUDIENCE
    }
  };
  try {
    return await getAccessTokenSilently(authParams);
  } catch (error) {
    return await getAccessTokenWithPopup(authParams);
  }
};

const createLangGraphClient = (apiUrl: string, token: string, userId: string) =>
  new Client({
    apiUrl,
    defaultHeaders: {
      Authorization: `Bearer ${token}`,
      'X-User-Id': userId
    }
  });

// Helper functions for generating tool call/result descriptions
const generateToolCallDescription = (
  toolName: string,
  toolArgs: Record<string, any>
): string => {
  const descriptions: Record<string, (args: any) => string> = {
    get_session_collection: () => '📂 Fetching collection information...',
    search_collection_summaries: (args) =>
      `🔍 Searching session summaries for: ${
        args.query?.slice(0, 50) || 'information'
      }...`,
    search_collection_insights: (args) =>
      `💡 Searching insights for: ${
        args.query?.slice(0, 50) || 'information'
      }...`,
    get_session_from_collection: () => '📄 Fetching session overview...',
    get_session_segments: (args) =>
      `📝 Loading segments ${args.offset || 0}-${
        (args.offset || 0) + (args.limit || 0)
      }...`
  };

  const descFn = descriptions[toolName] || (() => `⚙️ Calling ${toolName}...`);
  return descFn(toolArgs);
};

const generateToolResultDescription = (toolName: string): string => {
  const descriptions: Record<string, string> = {
    get_session_collection: 'Collection information retrieved',
    search_collection_summaries: 'Summary search complete',
    search_collection_insights: 'Insight search complete',
    get_session_from_collection: 'Session overview retrieved',
    get_session_segments: 'Segments loaded'
  };

  return descriptions[toolName] || `${toolName} complete`;
};

// Helper to process chunks: pair tool_calls with their results
// Only show the most recent tool_result (replace previous ones)
const processChunksForDisplay = (
  chunks: CollectionChatMessageChunk[]
): CollectionChatMessageChunk[] => {
  if (!chunks || chunks.length === 0) return [];

  // Build a map of tool names to their result chunks (keep only the latest)
  const toolResultMap = new Map<string, CollectionChatMessageChunk>();
  chunks.forEach((chunk) => {
    if (chunk.chunkType === 'tool_result' && chunk.toolName) {
      // Only keep the latest result for each tool
      const existing = toolResultMap.get(chunk.toolName);
      if (!existing || (chunk.sequence || 0) > (existing.sequence || 0)) {
        toolResultMap.set(chunk.toolName, chunk);
      }
    }
  });

  // Find the most recent tool_result overall (to show only one status)
  let latestToolResult: CollectionChatMessageChunk | null = null;
  toolResultMap.forEach((chunk) => {
    if (!latestToolResult || (chunk.sequence || 0) > (latestToolResult.sequence || 0)) {
      latestToolResult = chunk;
    }
  });

  // Filter chunks: skip tool_calls that have a corresponding tool_result
  // Only include the latest tool_result
  const displayChunks: CollectionChatMessageChunk[] = [];
  chunks.forEach((chunk) => {
    if (
      chunk.chunkType === 'tool_call' &&
      chunk.toolName &&
      toolResultMap.has(chunk.toolName)
    ) {
      // Skip this tool_call because we have a result for it
      return;
    }
    // Only include the latest tool_result, skip older ones
    if (chunk.chunkType === 'tool_result') {
      if (latestToolResult && chunk.id === latestToolResult.id) {
        displayChunks.push(chunk);
      }
      return;
    }
    // Include tool_calls without results
    if (chunk.chunkType === 'tool_call') {
      displayChunks.push(chunk);
    }
  });

  return displayChunks;
};

// Component to render tool call/result chunks (Cursor-style)
const ToolChunk: React.FC<{
  chunk: CollectionChatMessageChunk;
  theme: any;
}> = ({ chunk, theme }) => {
  const isToolCall = chunk.chunkType === 'tool_call';
  const isToolResult = chunk.chunkType === 'tool_result';

  if (!isToolCall && !isToolResult) return null;

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 0.5,
        px: 1,
        py: 0.5,
        my: 0.5,
        borderRadius: 1,
        bgcolor: theme.palette.mode === 'dark'
          ? 'rgba(255, 255, 255, 0.05)'
          : 'rgba(0, 0, 0, 0.02)',
        border: '1px solid',
        borderColor: theme.palette.divider,
        fontSize: '0.8rem',
        width: 'fit-content',
        maxWidth: '100%'
      }}
    >
      {isToolCall && (
        <CircularProgress
          size={10}
          thickness={5}
          sx={{ color: theme.palette.text.secondary, flexShrink: 0 }}
        />
      )}
      {isToolResult && (
        <CheckCircleIcon
          sx={{
            fontSize: '0.875rem',
            color: theme.palette.text.secondary,
            flexShrink: 0
          }}
        />
      )}
      <Typography
        variant="caption"
        sx={{
          fontSize: '0.75rem',
          fontWeight: 500,
          color: theme.palette.text.secondary,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis'
        }}
      >
        {chunk.content}
      </Typography>
    </Box>
  );
};

export const CollectionMultiChatUI: React.FC<CollectionMultiChatUIProps> = ({
  collectionId,
  compact = false
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { getAccessTokenSilently, getAccessTokenWithPopup } = useAuth0();
  const { appUser } = useAppUser();

  // Sidebar state
  const [sidebarOpen, setSidebarOpen] = useState(!isMobile);
  const [conversations, setConversations] = useState<CollectionConversation[]>(
    []
  );
  const [selectedConversationId, setSelectedConversationId] = useState<
    string | null
  >(null);
  const [loadingConversations, setLoadingConversations] = useState(true);

  // Chat state
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [isSending, setIsSending] = useState(false);

  // Creating conversation state
  const [creatingConversation, setCreatingConversation] = useState(false);
  const [justCreatedConversation, setJustCreatedConversation] = useState(false);

  // Context menu
  const [contextMenuAnchor, setContextMenuAnchor] =
    useState<null | HTMLElement>(null);
  const [contextMenuConversation, setContextMenuConversation] =
    useState<CollectionConversation | null>(null);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Load conversations
  const loadConversations = useCallback(async () => {
    if (!appUser?.id) return;

    setLoadingConversations(true);
    try {
      const token = await getAuthToken(
        getAccessTokenSilently,
        getAccessTokenWithPopup
      );

      const convs = await getCollectionConversations(
        collectionId,
        token,
        appUser.id
      );
      // Sort by lastMessageAt descending (most recent first)
      // Backend should already sort, but ensure it's sorted on frontend too
      const sortedConvs = convs.sort(
        (a, b) =>
          new Date(b.lastMessageAt).getTime() -
          new Date(a.lastMessageAt).getTime()
      );
      setConversations(sortedConvs);
    } catch (error) {
      console.error('Error loading conversations:', error);
    } finally {
      setLoadingConversations(false);
    }
  }, [
    collectionId,
    appUser?.id,
    getAccessTokenSilently,
    getAccessTokenWithPopup
  ]);

  // Load conversations on mount
  useEffect(() => {
    loadConversations();
  }, [loadConversations]);

  // Auto-create conversation in compact mode if none exists
  useEffect(() => {
    if (compact && !selectedConversationId && conversations.length === 0 && !loadingConversations && !creatingConversation && appUser?.id) {
      handleNewChat();
    }
  }, [compact, selectedConversationId, conversations.length, loadingConversations, creatingConversation, appUser?.id]);

  // Auto-select first conversation only on initial load
  useEffect(() => {
    if (
      !selectedConversationId &&
      conversations.length > 0 &&
      !loadingConversations
    ) {
      // Only auto-select if we haven't explicitly cleared selection (i.e., on first load)
      const hasExplicitlyCleared = sessionStorage.getItem(
        'collection-chat-new-chat'
      );
      if (!hasExplicitlyCleared) {
        setSelectedConversationId(conversations[0].id);
      }
    }
  }, [conversations, selectedConversationId, loadingConversations]);

  // Load messages for selected conversation
  useEffect(() => {
    if (!selectedConversationId || !appUser?.id) {
      setMessages([]);
      return;
    }

    // Don't reload messages if we just created this conversation and are sending the first message
    if (justCreatedConversation) {
      return;
    }

    const loadMessages = async () => {
      try {
        const token = await getAuthToken(
          getAccessTokenSilently,
          getAccessTokenWithPopup
        );

        const conv = await getConversationById(
          selectedConversationId,
          token,
          appUser.id
        );

        // Convert CollectionChatMessage to ChatMessage (including chunks)
        const chatMessages: ChatMessage[] = conv.messages.map((msg) => ({
          id: msg.id,
          role: msg.role === CollectionChatRole.User ? 'user' : 'assistant',
          content: msg.content,
          timestamp: msg.timestamp,
          chunks: msg.chunks || []
        }));

        setMessages(chatMessages);
      } catch (error) {
        console.error('Error loading messages:', error);
      }
    };

    loadMessages();
    // Note: justCreatedConversation is NOT in the dependency array
    // We only want this to run when the conversation ID changes or on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectedConversationId,
    appUser?.id,
    getAccessTokenSilently,
    getAccessTokenWithPopup
  ]);

  // Create new conversation with default title
  const createNewConversation = async (): Promise<CollectionConversation> => {
    if (!appUser?.id) {
      throw new Error('User not authenticated');
    }

    setCreatingConversation(true);
    setJustCreatedConversation(true);
    try {
      const token = await getAuthToken(
        getAccessTokenSilently,
        getAccessTokenWithPopup
      );

      const newConv = await createConversation(
        {
          title: 'New Conversation',
          description: null,
          sessionCollectionId: collectionId
        },
        token,
        appUser.id
      );

      setConversations((prev) => [newConv, ...prev]);
      setSelectedConversationId(newConv.id);
      // Clear the "new chat" flag since we now have a conversation
      sessionStorage.removeItem('collection-chat-new-chat');
      return newConv;
    } catch (error) {
      console.error('Error creating conversation:', error);
      setJustCreatedConversation(false);
      throw error;
    } finally {
      setCreatingConversation(false);
    }
  };

  // Handle "New Chat" button - just clear selection to show empty state
  const handleNewChat = () => {
    setSelectedConversationId(null);
    setMessages([]);
    // Mark that user explicitly wants new chat (prevent auto-select)
    sessionStorage.setItem('collection-chat-new-chat', 'true');
    if (isMobile) {
      setSidebarOpen(false);
    }
  };

  // Handle conversation deletion
  const handleDeleteConversation = async (
    conversation: CollectionConversation
  ) => {
    if (!appUser?.id) return;

    try {
      const token = await getAuthToken(
        getAccessTokenSilently,
        getAccessTokenWithPopup
      );

      await deleteConversation(conversation.id, token, appUser.id);

      // Remove from conversations list
      setConversations((prev) => prev.filter((c) => c.id !== conversation.id));

      // If this was the selected conversation, clear selection
      if (selectedConversationId === conversation.id) {
        setSelectedConversationId(null);
        setMessages([]);
      }
    } catch (error) {
      console.error('Error deleting conversation:', error);
      // Optionally show error notification to user
    }
  };

  // Send message
  const handleSend = useCallback(async () => {
    if (!input.trim() || isSending || !appUser?.id) return;

    const content = input.trim();
    setInput('');
    setIsSending(true);

    // If no conversation selected, create one first
    let conversationId = selectedConversationId;
    let newlyCreatedConversation: CollectionConversation | null = null;

    if (!conversationId) {
      try {
        newlyCreatedConversation = await createNewConversation();
        conversationId = newlyCreatedConversation.id;
        console.log('Created new conversation:', conversationId);
      } catch (error) {
        console.error('Failed to create conversation:', error);
        setInput(content); // Restore input
        setIsSending(false);
        return;
      }
    }

    const assistantMessageId = uuidv4();

    try {
      const token = await getAuthToken(
        getAccessTokenSilently,
        getAccessTokenWithPopup
      );

      abortControllerRef.current = new AbortController();
      const client = createLangGraphClient(
        LANGGRAPH_BASE_URL,
        token,
        appUser.id
      );

      // Add user message
      const userMessage: ChatMessage = {
        id: uuidv4(),
        role: 'user',
        content,
        timestamp: new Date().toISOString()
      };
      setMessages((prev) => [...prev, userMessage]);

      // Add streaming assistant message
      const assistantMessage: ChatMessage = {
        id: assistantMessageId,
        role: 'assistant',
        content: '',
        timestamp: new Date().toISOString(),
        isStreaming: true
      };
      setMessages((prev) => [...prev, assistantMessage]);

      // Get or create thread ID for this conversation
      // Use newly created conversation if we just made one, otherwise find it in the list
      const selectedConv =
        newlyCreatedConversation ||
        conversations.find((c) => c.id === conversationId);
      let threadId = selectedConv?.threadId;

      console.log(
        'Sending message to conversation:',
        conversationId,
        'threadId:',
        threadId
      );

      if (!threadId) {
        const createdThread = await client.threads.create();
        threadId = createdThread.thread_id;
        
        // Save thread ID to database
        try {
          await updateConversationThreadId(conversationId, threadId, token, appUser.id);
          console.log('[CollectionMultiChatUI] Saved thread ID to conversation:', threadId);
          
          // Update local conversation state
          setConversations((prev) =>
            prev.map((c) =>
              c.id === conversationId ? { ...c, threadId } : c
            )
          );
        } catch (error) {
          console.error('[CollectionMultiChatUI] Failed to save thread ID to database:', error);
          // Continue anyway - the thread will still work, just won't be persisted
        }
      }

      // Stream from the collection_chat_agent
      // Note: backend_url is NOT passed - agent gets it from BACKEND_API_URL env var
      const stream = client.runs.stream(threadId, 'collection_chat_agent', {
        input: {
          messages: [{ type: 'human', content }]
        },
        config: {
          configurable: {
            auth_token: token,
            bearer_token: token,
            collection_id: collectionId,
            conversation_id: conversationId,
            user_id: appUser.id
          }
        },
        metadata: {
          collection_id: collectionId,
          conversation_id: conversationId,
          thread_id: threadId,
          user_id: appUser.id
        },
        streamMode: 'messages-tuple',
        streamSubgraphs: true,
        signal: abortControllerRef.current.signal
      });

      let accumulatedContent = '';
      let chunkSequence = 0;
      const streamingChunks: CollectionChatMessageChunk[] = [];
      const processedToolCalls = new Set<string>();

      for await (const chunk of stream as any) {
        // Handle messages-tuple mode: data = [message, metadata]
        if (chunk.event === 'messages') {
          const data = chunk.data;
          if (Array.isArray(data) && data.length === 2) {
            const [msg, meta] = data as [any, any];

            // Skip summarization middleware chunks
            if (meta?.langgraph_node?.startsWith('SummarizationMiddleware')) {
              console.log('Skipping summarization middleware chunk');
              continue;
            }

            // Handle tool calls
            if (msg?.tool_calls && Array.isArray(msg.tool_calls)) {
              for (const toolCall of msg.tool_calls) {
                const toolId = toolCall.id || '';
                if (toolId && !processedToolCalls.has(toolId)) {
                  processedToolCalls.add(toolId);
                  chunkSequence++;

                  const toolCallChunk: CollectionChatMessageChunk = {
                    id: `chunk-${chunkSequence}`,
                    sequence: chunkSequence,
                    chunkType: 'tool_call',
                    content: generateToolCallDescription(
                      toolCall.name,
                      toolCall.args || {}
                    ),
                    toolName: toolCall.name,
                    toolArgs: JSON.stringify(toolCall.args || {}),
                    toolResult: null,
                    timestamp: new Date().toISOString()
                  };

                  streamingChunks.push(toolCallChunk);

                  setMessages((prev) => {
                    const updated = [...prev];
                    const assistantMsgIdx = updated.findIndex(
                      (m) =>
                        m.id === assistantMessageId && m.role === 'assistant'
                    );
                    if (assistantMsgIdx >= 0) {
                      updated[assistantMsgIdx] = {
                        ...updated[assistantMsgIdx],
                        chunks: [...streamingChunks],
                        isStreaming: true
                      };
                    }
                    return updated;
                  });
                }
              }
            }

            // Handle tool results
            if (msg?.type === 'tool' && msg?.name) {
              chunkSequence++;

              const toolResultChunk: CollectionChatMessageChunk = {
                id: `chunk-${chunkSequence}`,
                sequence: chunkSequence,
                chunkType: 'tool_result',
                content: generateToolResultDescription(msg.name),
                toolName: msg.name,
                toolArgs: null,
                toolResult:
                  typeof msg.content === 'string'
                    ? msg.content
                    : JSON.stringify(msg.content),
                timestamp: new Date().toISOString()
              };

              streamingChunks.push(toolResultChunk);

              setMessages((prev) => {
                const updated = [...prev];
                const assistantMsgIdx = updated.findIndex(
                  (m) => m.id === assistantMessageId && m.role === 'assistant'
                );
                if (assistantMsgIdx >= 0) {
                  updated[assistantMsgIdx] = {
                    ...updated[assistantMsgIdx],
                    chunks: [...streamingChunks],
                    isStreaming: true
                  };
                }
                return updated;
              });
            }

            // Process AI message content
            if (msg?.type === 'ai' || msg?.role === 'assistant') {
              const newContent = msg.content || '';
              if (newContent && newContent !== accumulatedContent) {
                // Handle incremental text
                const incrementalText = newContent.slice(
                  accumulatedContent.length
                );
                accumulatedContent = newContent;

                // Add or update text chunk
                const lastChunk = streamingChunks[streamingChunks.length - 1];
                if (lastChunk && lastChunk.chunkType === 'text') {
                  // Append to existing text chunk
                  lastChunk.content += incrementalText;
                } else {
                  // Create new text chunk
                  chunkSequence++;
                  streamingChunks.push({
                    id: `chunk-${chunkSequence}`,
                    sequence: chunkSequence,
                    chunkType: 'text',
                    content: incrementalText,
                    toolName: null,
                    toolArgs: null,
                    toolResult: null,
                    timestamp: new Date().toISOString()
                  });
                }

                setMessages((prev) => {
                  const updated = [...prev];
                  const assistantMsgIdx = updated.findIndex(
                    (m) => m.id === assistantMessageId && m.role === 'assistant'
                  );
                  if (assistantMsgIdx >= 0) {
                    updated[assistantMsgIdx] = {
                      ...updated[assistantMsgIdx],
                      content: accumulatedContent,
                      chunks: [...streamingChunks],
                      isStreaming: true
                    };
                  }
                  return updated;
                });
              }
            }
          }
        }
      }

      // Finalize the streaming message
      setMessages((prev) => {
        const updated = [...prev];
        const assistantMsgIdx = updated.findIndex(
          (m) => m.id === assistantMessageId && m.role === 'assistant'
        );
        if (assistantMsgIdx >= 0) {
          updated[assistantMsgIdx] = {
            ...updated[assistantMsgIdx],
            isStreaming: false
          };
        }
        return updated;
      });

      // Update conversation list only if we didn't just create it
      // (For new conversations, it's already in the list; for existing ones, update message count)
      if (!newlyCreatedConversation) {
        await loadConversations();
      }
    } catch (error: any) {
      if (error.name === 'AbortError') {
        console.log('Chat request aborted');
      } else {
        console.error('Error sending chat message:', error);
        setMessages((prev) => {
          const updated = [...prev];
          const assistantMsgIdx = updated.findIndex(
            (m) => m.id === assistantMessageId && m.role === 'assistant'
          );
          if (assistantMsgIdx >= 0) {
            updated[assistantMsgIdx] = {
              ...updated[assistantMsgIdx],
              content: 'Sorry, I encountered an error. Please try again.',
              isStreaming: false
            };
          }
          return updated;
        });
      }
    } finally {
      setIsSending(false);
      setJustCreatedConversation(false);
      abortControllerRef.current = null;
    }
  }, [
    input,
    isSending,
    appUser?.id,
    collectionId,
    selectedConversationId,
    conversations,
    getAccessTokenSilently,
    getAccessTokenWithPopup,
    loadConversations,
    createNewConversation
  ]);

  const canSend = useMemo(
    () =>
      !isSending &&
      !creatingConversation &&
      input.trim().length > 0 &&
      !!appUser?.id,
    [isSending, creatingConversation, input, appUser?.id]
  );

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey && canSend) {
      e.preventDefault();
      handleSend();
    }
  };

  const selectedConversation = conversations.find(
    (c) => c.id === selectedConversationId
  );

  return (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        bgcolor: compact ? 'background.paper' : 'background.default',
        position: 'relative'
      }}
    >
      {/* Sidebar - Hidden in compact mode */}
      {!compact && (
        <Drawer
          variant={isMobile ? 'temporary' : 'persistent'}
          open={sidebarOpen}
          onClose={() => setSidebarOpen(false)}
        sx={{
          width: sidebarOpen ? SIDEBAR_WIDTH : 0,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: SIDEBAR_WIDTH,
            boxSizing: 'border-box',
            position: 'relative',
            height: '100%',
            borderRight: `1px solid ${theme.palette.divider}`,
            bgcolor: theme.palette.background.paper
          }
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          {/* New Chat Button */}
          <Box sx={{ p: 2 }}>
            <Button
              fullWidth
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleNewChat}
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 500
              }}
            >
              New Chat
            </Button>
          </Box>

          <Divider />

          {/* Conversation List */}
          <Box sx={{ flex: 1, overflowY: 'auto' }}>
            {loadingConversations ? (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  py: 4
                }}
              >
                <CircularProgress size={24} />
              </Box>
            ) : conversations.length === 0 ? (
              <Box sx={{ px: 2, py: 4, textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  No conversations yet
                </Typography>
                <Typography variant="caption" color="text.disabled">
                  Create a new chat to get started
                </Typography>
              </Box>
            ) : (
              <List sx={{ py: 1 }}>
                {conversations.map((conv) => (
                  <ListItem key={conv.id} disablePadding sx={{ px: 1 }}>
                    <ListItemButton
                      selected={conv.id === selectedConversationId}
                      onClick={() => {
                        setSelectedConversationId(conv.id);
                        if (isMobile) setSidebarOpen(false);
                      }}
                      sx={{
                        borderRadius: 1,
                        mb: 0.5,
                        '&.Mui-selected': {
                          bgcolor:
                            theme.palette.mode === 'dark'
                              ? 'rgba(144, 202, 249, 0.16)'
                              : 'rgba(25, 118, 210, 0.08)'
                        }
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        <ChatIcon fontSize="small" />
                      </ListItemIcon>
                      <ListItemText
                        primary={conv.title}
                        secondary={`${conv.messageCount} messages`}
                        primaryTypographyProps={{
                          fontSize: '14px',
                          fontWeight: 500,
                          noWrap: true
                        }}
                        secondaryTypographyProps={{
                          fontSize: '12px'
                        }}
                      />
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          setContextMenuAnchor(e.currentTarget);
                          setContextMenuConversation(conv);
                        }}
                        sx={{ ml: 1, borderRadius: '6px' }}
                      >
                        <MoreVertIcon fontSize="small" />
                      </IconButton>
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            )}
          </Box>
        </Box>
      </Drawer>
      )}

      {/* Main Chat Area */}
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          bgcolor: 'background.paper'
        }}
      >
        {/* Chat Header - Hidden in compact mode */}
        {!compact && (
          <Box
            sx={{
              p: 2,
              borderBottom: `1px solid ${theme.palette.divider}`,
              display: 'flex',
              alignItems: 'center',
              gap: 2
            }}
          >
            {isMobile && (
              <IconButton onClick={() => setSidebarOpen(true)} size="small">
                <MenuIcon />
              </IconButton>
            )}
            <Box sx={{ flex: 1 }}>
              <Typography variant="h6" sx={{ fontSize: '16px', fontWeight: 600 }}>
                {selectedConversation?.title || 'Select a conversation'}
              </Typography>
              {selectedConversation && (
                <Typography variant="caption" color="text.secondary">
                  {format(parseISO(selectedConversation.createdAt), 'PPp')}
                </Typography>
              )}
            </Box>
          </Box>
        )}

        {/* Messages Area */}
        <Box
          sx={{
            flex: 1,
            overflowY: 'auto',
            p: { xs: 2, sm: 3 },
            display: 'flex',
            flexDirection: 'column',
            gap: 2
          }}
        >
          {!selectedConversationId ? (
            !compact ? (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: '100%',
                  gap: 2,
                  color: 'text.secondary'
                }}
              >
                <BotIcon sx={{ fontSize: 64, opacity: 0.3 }} />
                <Typography variant="h6" sx={{ textAlign: 'center' }}>
                  Welcome to Collection Chat
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ textAlign: 'center', maxWidth: 400 }}
                >
                  Start asking questions about this collection.
                  <br />I can search across all sessions and help you understand
                  the case.
                </Typography>
              </Box>
            ) : (
              <Box sx={{ height: '100%' }} />
            )
          ) : messages.length === 0 ? (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
                gap: 2,
                color: 'text.secondary'
              }}
            >
              <BotIcon sx={{ fontSize: 48, opacity: 0.3 }} />
              <Typography variant="body2" sx={{ textAlign: 'center' }}>
                Start a new conversation
                <br />
                Ask me anything about this collection!
              </Typography>
              <Typography
                variant="caption"
                sx={{ textAlign: 'center', mt: 1, maxWidth: 400 }}
              >
                I can search across all sessions, find patterns, and help you
                understand the case.
              </Typography>
            </Box>
          ) : (
            messages.map((message) => (
              <Box
                key={message.id}
                sx={{
                  width: '100%',
                  mb: message.role === 'assistant' ? 2.5 : 1.5
                }}
              >
                {message.role === 'user' ? (
                  // User message: small dialog box, right-aligned
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'flex-end',
                      width: '100%'
                    }}
                  >
                    <Paper
                      elevation={0}
                      sx={{
                        maxWidth: '75%',
                        p: 1.25,
                        px: 1.5,
                        bgcolor:
                          theme.palette.mode === 'dark'
                            ? 'rgba(255,255,255,0.08)'
                            : '#f3f4f6',
                        border: `1px solid ${theme.palette.divider}`,
                        borderRadius: '8px',
                        position: 'relative'
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: '13px',
                          lineHeight: 1.5,
                          color: theme.palette.text.primary,
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-word'
                        }}
                      >
                        {message.content}
                      </Typography>
                    </Paper>
                  </Box>
                ) : (
                  // Assistant message: plain text below
                  <Box
                    sx={{
                      width: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 0.5
                    }}
                  >
                    {/* Tool Chunks - always show if present */}
                    {message.chunks && message.chunks.length > 0 && (
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 0.5,
                          mb: message.content ? 1 : 0
                        }}
                      >
                        {processChunksForDisplay(message.chunks).map(
                          (chunk) => (
                            <ToolChunk
                              key={chunk.id}
                              chunk={chunk}
                              theme={theme}
                            />
                          )
                        )}
                      </Box>
                    )}

                    {/* Loading indicator - show only when streaming with no content and no chunks */}
                    {message.isStreaming &&
                      !message.content &&
                      (!message.chunks || message.chunks.length === 0) && (
                        <Box sx={{ pl: 0 }}>
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 0.5,
                              py: 1
                            }}
                          >
                            <Box
                              sx={{
                                width: 8,
                                height: 8,
                                borderRadius: '50%',
                                bgcolor: 'text.secondary',
                                animation: 'typing-dot 1.4s infinite',
                                animationDelay: '0s',
                                '@keyframes typing-dot': {
                                  '0%, 60%, 100%': { opacity: 0.3 },
                                  '30%': { opacity: 1 }
                                }
                              }}
                            />
                            <Box
                              sx={{
                                width: 8,
                                height: 8,
                                borderRadius: '50%',
                                bgcolor: 'text.secondary',
                                animation: 'typing-dot 1.4s infinite',
                                animationDelay: '0.2s',
                                '@keyframes typing-dot': {
                                  '0%, 60%, 100%': { opacity: 0.3 },
                                  '30%': { opacity: 1 }
                                }
                              }}
                            />
                            <Box
                              sx={{
                                width: 8,
                                height: 8,
                                borderRadius: '50%',
                                bgcolor: 'text.secondary',
                                animation: 'typing-dot 1.4s infinite',
                                animationDelay: '0.4s',
                                '@keyframes typing-dot': {
                                  '0%, 60%, 100%': { opacity: 0.3 },
                                  '30%': { opacity: 1 }
                                }
                              }}
                            />
                          </Box>
                        </Box>
                      )}

                    {/* Text Content */}
                    {message.content && (
                      <Box
                        sx={{
                          fontSize: '14px',
                          lineHeight: 1.7,
                          color: theme.palette.text.primary,
                          textAlign: 'left',
                          '& p': { m: 0, textAlign: 'left', mb: 1.5 },
                          '& p:last-child': { mb: 0 },
                          '& ul, & ol': { my: 1.5, pl: 2.5, textAlign: 'left' },
                          '& li': { my: 0.75, textAlign: 'left' },
                          '& h1, & h2, & h3, & h4, & h5, & h6': {
                            mt: 2,
                            mb: 1,
                            textAlign: 'left',
                            fontWeight: 600
                          },
                          '& h1': { fontSize: '1.5em' },
                          '& h2': { fontSize: '1.3em' },
                          '& h3': { fontSize: '1.1em' },
                          '& code': {
                            bgcolor:
                              theme.palette.mode === 'dark'
                                ? 'rgba(255,255,255,0.1)'
                                : 'rgba(0,0,0,0.06)',
                            px: 0.5,
                            py: 0.25,
                            borderRadius: 0.375,
                            fontSize: '0.9em',
                            fontFamily: 'monospace'
                          },
                          '& pre': {
                            bgcolor:
                              theme.palette.mode === 'dark'
                                ? 'rgba(255,255,255,0.05)'
                                : 'rgba(0,0,0,0.03)',
                            p: 1.5,
                            borderRadius: 0.5,
                            overflow: 'auto',
                            my: 1.5,
                            '& code': {
                              bgcolor: 'transparent',
                              p: 0
                            }
                          },
                          '& blockquote': {
                            borderLeft: `3px solid ${theme.palette.divider}`,
                            pl: 1.5,
                            my: 1.5,
                            fontStyle: 'italic',
                            color: theme.palette.text.secondary
                          },
                          '& a': {
                            color: theme.palette.primary.main,
                            textDecoration: 'none',
                            '&:hover': {
                              textDecoration: 'underline'
                            }
                          }
                        }}
                      >
                        <ReactMarkdown>{message.content}</ReactMarkdown>
                      </Box>
                    )}
                  </Box>
                )}
              </Box>
            ))
          )}

          <div ref={messagesEndRef} />
        </Box>

        {/* Input Area */}
        <Box
          sx={{
            p: 2,
            borderTop: `1px solid ${theme.palette.divider}`,
            bgcolor: theme.palette.background.paper
          }}
        >
          <Box
            sx={{
              display: 'flex',
              gap: 1,
              alignItems: 'flex-end',
              p: 1.5,
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: '12px',
              backgroundColor:
                theme.palette.mode === 'dark'
                  ? 'rgba(255,255,255,0.03)'
                  : '#fafafa',
              transition: 'all 0.2s ease',
              '&:focus-within': {
                borderColor: '#6b7280',
                backgroundColor: theme.palette.background.paper
              }
            }}
          >
            <TextField
              fullWidth
              multiline
              maxRows={4}
              placeholder="Ask about this folder..."
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyPress={handleKeyPress}
              disabled={isSending || creatingConversation}
              variant="standard"
              inputRef={inputRef}
              InputProps={{
                disableUnderline: true,
                sx: {
                  fontSize: '13px',
                  lineHeight: 1.5,
                  '& textarea': {
                    '&::placeholder': {
                      color: theme.palette.text.secondary,
                      opacity: 0.7
                    }
                  }
                }
              }}
            />
            <IconButton
              onClick={handleSend}
              disabled={!canSend}
              size="small"
              sx={{
                width: 28,
                height: 28,
                bgcolor: canSend
                  ? '#1a1a1a'
                  : theme.palette.action.disabledBackground,
                color: canSend ? 'white' : theme.palette.action.disabled,
                borderRadius: '6px',
                flexShrink: 0,
                '&:hover': {
                  bgcolor: canSend
                    ? '#000'
                    : theme.palette.action.disabledBackground
                },
                '&:disabled': {
                  bgcolor: theme.palette.action.disabledBackground
                }
              }}
            >
              {isSending || creatingConversation ? (
                <CircularProgress size={14} sx={{ color: 'inherit' }} />
              ) : (
                <ArrowUp size={16} weight="bold" />
              )}
            </IconButton>
          </Box>
        </Box>
      </Box>

      {/* Context Menu */}
      <Menu
        anchorEl={contextMenuAnchor}
        open={Boolean(contextMenuAnchor)}
        onClose={() => {
          setContextMenuAnchor(null);
          setContextMenuConversation(null);
        }}
      >
        <MenuItem
          onClick={async () => {
            if (contextMenuConversation) {
              await handleDeleteConversation(contextMenuConversation);
            }
            setContextMenuAnchor(null);
            setContextMenuConversation(null);
          }}
          sx={{ color: 'error.main' }}
        >
          <ListItemIcon>
            <DeleteIcon fontSize="small" color="error" />
          </ListItemIcon>
          Delete Conversation
        </MenuItem>
      </Menu>
    </Box>
  );
};
